import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '@/lib/utils';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE FIRST COLUMN IS THE WAY IN.
 *
 *  In every list, clicking a row's NAME opens whatever that row's primary surface is —
 *  a detail route where one exists, otherwise the panel that shows it. It is the
 *  shortest path from "I can see it" to "I am looking at it", and an operator should
 *  never have to hunt along a row to an `Edit` button to get there.
 *
 *  ⚠️ IT OPENS, IT DOES NOT EDIT. The distinction matters for a read-only role: the name
 *  is ALWAYS clickable, and what it opens is read-only when the capability is missing.
 *  The write action stays where it was, in the row's controls, disabled with a tooltip —
 *  so a nodal still learns their access level from the screen (§10) while keeping the
 *  ability to look.
 *
 *  ⚠️ A ROUTE TARGET RENDERS A REAL `<a>`, NOT A BUTTON, and that is not pedantry: a
 *  button cannot be ⌘-clicked into a new tab, cannot be copied as a link, and shows no
 *  destination in the status bar. Anything that navigates is a link; anything that opens
 *  a panel in place is a button. `NameCell` picks by which prop it was given, so the
 *  choice cannot be made wrongly at a call site.
 *
 *  The secondary line sits OUTSIDE the control. Inside it, the accessible name of every
 *  row becomes "Esplanade also: Chowringhee" — which breaks `getByRole('link', { name })`
 *  and reads badly aloud.
 *
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [D4.4 §7a] IT TRUNCATES, AND THAT IS A NARROW EXCEPTION TO §8's "CELLS NEVER
 *  TRUNCATE" RATHER THAN A REVERSAL OF IT.
 *
 *  A long arrangement name ran into its neighbour. The `truncate` class was already here
 *  and did nothing: under `table-layout: auto` with `scroll={{ x: 'max-content' }}` the
 *  cell simply grows to fit its content, so there is no width for the ellipsis to happen
 *  at. `maxWidth` on this element supplies one.
 *
 *  ⚠️ §8's RULE IS ABOUT AntD's `ellipsis` PROP, AND THIS IS NOT THAT. That prop flips
 *  `tableLayout` to `fixed` and redistributes EVERY column's width across the table; a
 *  max-width on one cell's own content flips nothing, and the table still scrolls. The
 *  invariant §8 protects — no column silently compressed, no data cell clipped — is intact.
 *
 *  ⚠️ AND IT IS THE FIRST COLUMN ONLY, FOR A REASON THAT DOES NOT GENERALISE: the name is
 *  the one cell whose full content is reachable by another route, because it IS the link to
 *  the record. A data cell has no such fallback, which is why they still never truncate.
 *
 *  ⚠️ THE FULL TEXT IS NOT POINTER-ONLY. The link's text content is the whole name —
 *  truncation is visual, so a screen reader and `getByRole('link', { name })` both still
 *  see all of it — and `title` adds hover on top. The detail page behind the link is the
 *  third route to it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type NameCellProps = {
  name: string;
  /**
   * A CSS length capping the name's measure, e.g. `'22rem'`. Omitted → no cap, which is
   * every existing call site's behaviour unchanged.
   *
   * ⚠️ A STYLE RATHER THAN A TAILWIND CLASS, deliberately. An arbitrary-value class written
   * at a call site is a Tailwind candidate the extractor must find in source text, and
   * `lib/containerQueryCandidates.test.ts` records what happens when one appears somewhere
   * the build cannot resolve. A plain inline length has no build step to get wrong.
   */
  maxWidth?: string;
  /** A route. Renders a real link. Mutually exclusive with `onOpen`. */
  to?: string;
  /** Opens a panel in place. Renders a button. Mutually exclusive with `to`. */
  onOpen?: () => void;
  /** The muted second line — a short name, a description, alternative names. */
  secondary?: ReactNode;
  /**
   * ⚠️ [C5c §10] A FACT ABOUT THE ROW, BESIDE THE NAME AND OUTSIDE THE CONTROL — the raiser pill
   * ([amended P7A §2], formerly the HQ pill).
   *
   * The secondary line's rule, one slot over: inside the link it would join the accessible name
   * ("Esplanade on 15 Aug 2026 HQ") and every by-name query would stop finding the row. So it sits
   * in a row of its own with the control, `shrink-0`, and the control's `truncate` gives it
   * `overflow: hidden` — which makes the NAME the thing that gives way, never the badge.
   * `HierarchyTable`'s `tree.badge` is the same slot for a tree row.
   *
   * ⚠️ OMITTED → THE DOM IS BYTE-FOR-BYTE WHAT IT WAS. Every other call site passes none, and the
   * cap, the `title` and the link-is-the-cell's-child shape all belong to them.
   */
  badge?: ReactNode;
  className?: string;
};

/** Links are distinguished by an underline, not a hue — `theme/tokens.ts`, `link`. */
const CONTROL = 'truncate text-left text-link underline-offset-4 hover:underline';

export const NameCell = ({ name, to, onOpen, secondary, badge, className, maxWidth }: NameCellProps) => {
  const control =
    to !== undefined ? (
      <Link to={to} className={CONTROL} title={name}>
        {name}
      </Link>
    ) : onOpen ? (
      <button type="button" onClick={onOpen} className={CONTROL} title={name}>
        {name}
      </button>
    ) : (
      // No target: still the name, just not a control. A dead link is worse than plain
      // text, and this is what a row with nothing to open should look like.
      <span className="truncate" title={name}>
        {name}
      </span>
    );

  return (
    /* ⚠️ The cap goes on the WRAPPER, so the secondary line is bounded by the same measure —
       a `paNumber` is short, but an alternative-names list is not. */
    <div className={cn('flex min-w-0 flex-col', className)} style={maxWidth ? { maxWidth } : undefined}>
      {badge === undefined || badge === null || typeof badge === 'boolean' ? (
        control
      ) : (
        <span className="flex min-w-0 items-center gap-1.5">
          {control}
          <span className="shrink-0">{badge}</span>
        </span>
      )}
      {secondary !== undefined && secondary !== '' && (
        <span className="truncate text-xs text-muted-foreground">{secondary}</span>
      )}
    </div>
  );
};
